import React from 'react';
import './ToolState.css';

/** The eight data-tool states from the Design canvas artboard `web/states` (Trump canvas, page "Web platform").
 *  Every tool shows these inside its own card, under its own header, never as a full-page takeover.
 *  Rendered only when the Design System preview flag is on (callers check isDesignSystemOn()); the CSS is
 *  scoped to html[data-mmec-ds="1"] as well. */
export type ToolStateKind = 'loading' | 'empty' | 'warming' | 'gate' | 'expired' | 'stale' | 'thin' | 'error';

type Lang = 'en' | 'ar';

const COPY: Record<ToolStateKind, Record<Lang, { tag?: string; title: string; body: string; action?: string }>> = {
  loading: { en: { title: 'Loading', body: '' }, ar: { title: 'جارٍ التحميل', body: '' } },
  empty: {
    en: { title: 'No data in table', body: 'Awaiting real-time data connection' },
    ar: { title: 'لا توجد بيانات في الجدول', body: 'في انتظار اتصال البيانات في الوقت الفعلي' },
  },
  warming: {
    en: { tag: 'Building', title: 'Building the chain… about 3 min', body: 'The feed restarted. Levels and tables fill in as the chain rebuilds; nothing to do.' },
    ar: { tag: 'قيد البناء', title: 'جارٍ بناء السلسلة… نحو 3 دقائق', body: 'أُعيد تشغيل التغذية. تمتلئ المستويات والجداول مع إعادة بناء السلسلة؛ لا شيء مطلوب منك.' },
  },
  gate: {
    en: { tag: 'Too many tabs', title: 'Too many tabs for this login — close one to continue', body: 'Up to 3 open tabs per login. This tab reconnects on its own once one closes.', action: 'Check again' },
    ar: { tag: 'تبويبات كثيرة', title: 'عدد التبويبات المفتوحة لهذا الحساب كبير — أغلق واحداً للمتابعة', body: 'حتى 3 تبويبات مفتوحة لكل حساب. يعود هذا التبويب للاتصال تلقائياً عند إغلاق أحدها.', action: 'تحقّق مجدداً' },
  },
  expired: {
    en: { title: 'Your session ended', body: 'Sign in again to keep the feeds live; your layout and filters are kept.', action: 'Sign in again' },
    ar: { title: 'انتهت جلستك', body: 'سجّل الدخول مجدداً لتبقى البيانات مباشرة؛ يُحفظ تخطيطك وفلاترك.', action: 'تسجيل الدخول مجدداً' },
  },
  stale: {
    en: { tag: 'Stale', title: 'Live ticks paused', body: 'Showing the Stable feed until live ticks return.' },
    ar: { tag: 'متأخر', title: 'توقفت التحديثات المباشرة', body: 'نعرض التغذية المستقرة حتى تعود التحديثات المباشرة.' },
  },
  thin: {
    en: { tag: 'Thin', title: 'Thin option chain', body: 'These levels rest on few contracts and can move with every print.' },
    ar: { tag: 'سلسلة ضعيفة', title: 'سلسلة خيارات ضعيفة', body: 'هذه المستويات مبنية على عقود قليلة وقد تتحرك مع كل صفقة.' },
  },
  error: {
    en: { tag: 'Feed down', title: "The options feed isn't answering", body: 'We retry every 30 s. Other tools keep working; the last good snapshot stays visible where there is one.', action: 'Retry now' },
    ar: { tag: 'التغذية متوقفة', title: 'تغذية الخيارات لا تستجيب', body: 'نعيد المحاولة كل 30 ثانية. بقية الأدوات تعمل؛ ويبقى آخر عرض سليم ظاهراً إن وُجد.', action: 'أعد المحاولة الآن' },
  },
};

const TAG_TONE: Partial<Record<ToolStateKind, string>> = { warming: 'gold', gate: 'down', stale: 'gold', thin: 'gold', error: 'down' };

export interface ToolStateProps {
  kind: ToolStateKind;
  lang?: Lang;
  /** Override the default headline or body (e.g. a tool-specific empty message). */
  title?: React.ReactNode;
  body?: React.ReactNode;
  /** Primary action (retry, check again, sign in). Defaults per kind; omitted when no handler. */
  onAction?: () => void;
  actionLabel?: string;
  /** 0..1 for the warming bar. */
  progress?: number;
  /** Small mono line under the body ("next check in 15 s", "last tick 11 min ago"). */
  meta?: React.ReactNode;
  /** Number of skeleton rows for `loading`. */
  rows?: number;
  /** Fill the parent (flex: 1) instead of a fixed min height. */
  fill?: boolean;
  className?: string;
  /** Override the kind's tag; `null` hides it (e.g. a single tile's read error). */
  tag?: string | null;
}

export const ToolState: React.FC<ToolStateProps> = ({
  kind, lang = 'en', title, body, onAction, actionLabel, progress, meta, rows = 6, fill, className, tag,
}) => {
  const c = COPY[kind][lang] || COPY[kind].en;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const cls = `mmec-state mmec-state--${kind}${fill ? ' mmec-state--fill' : ''}${className ? ' ' + className : ''}`;
  if (kind === 'loading') {
    return (
      <div className={cls} dir={dir} role="status" aria-label={c.title} aria-busy="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div className="mmec-state__sk-row" key={i}>
            <span className="mmec-state__sk" style={{ width: 40 + (i % 3) * 16 }} />
            <span className="mmec-state__sk mmec-state__sk--grow" />
            <span className="mmec-state__sk" style={{ width: 48 }} />
          </div>
        ))}
      </div>
    );
  }
  const tone = TAG_TONE[kind];
  return (
    <div className={cls} dir={dir} role={kind === 'error' || kind === 'expired' ? 'alert' : 'status'}>
      {kind === 'empty' && (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mmec-state__icon">
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      )}
      {tag !== null && (tag ?? c.tag) && <span className={`mmec-tag${tone ? ' mmec-tag--' + tone : ''}`}>{tag ?? c.tag}</span>}
      <span className="mmec-state__title">{title ?? c.title}</span>
      {(body ?? c.body) && <span className="mmec-state__body">{body ?? c.body}</span>}
      {kind === 'warming' && typeof progress === 'number' && (
        <div className="mmec-state__bar" aria-hidden="true"><div style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} /></div>
      )}
      {meta && <span className="mmec-state__meta mmec-mono">{meta}</span>}
      {onAction && (actionLabel || c.action) && (
        <button type="button" className={`mmec-btn${kind === 'expired' || kind === 'error' ? ' mmec-btn--primary' : ''}${kind === 'expired' ? ' mmec-btn--lg' : ''}`} onClick={onAction}>
          {actionLabel || c.action}
        </button>
      )}
    </div>
  );
};

/** Inline thin-chain / stale badge for tool headers (artboard: "NDX levels [Thin]", "RTL · stale"). */
export const ToolStateBadge: React.FC<{ kind: 'thin' | 'stale'; lang?: Lang; meta?: React.ReactNode; title?: string }> = ({ kind, lang = 'en', meta, title }) => {
  const c = COPY[kind][lang] || COPY[kind].en;
  return (
    <span className="mmec-state-badge" title={title || c.body}>
      <span className="mmec-tag mmec-tag--gold">{c.tag}</span>
      {meta && <span className="mmec-mono mmec-state__meta">{meta}</span>}
    </span>
  );
};

export default ToolState;
